import { WorkflowConversationPresentation } from './WorkflowConversationPresentation';
import { normalizeHomeChatCards } from './types';
import { mergeHomeChatConversation } from './homeChatConversation';
import { getWorkflowOnlyPresentation } from './workflowOnlyPresentation';
import PurchaseFollowupInput from './PurchaseFollowupInput';
import { useState } from 'react';
import { getWorkflowNotificationObserverProgress, getWorkflowNotificationResumeProgress } from '../../api/workflowNotifications';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import SimilarRequirementsCards from './SimilarRequirementsCards';
import MessageInputShell from '../FarChat/MessageInputShell';
import HomeChatWorkflowBanner from './HomeChatWorkflowBanner';
import FarChatResponse from '../FarChat/FarChatResponse';
import CircularProgress from '@mui/material/CircularProgress';
import ChatThread from '../FarChat/ChatThread';
import Button from '@mui/material/Button';
import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import GpcChatPanel from '../GpcChat/GpcChatPanel';
import MrChatPanel from '../MrChat/MrChatPanel';
import ConstructionSowChatPanel from '../ConstructionSowChat/ConstructionSowChatPanel';
import ServicesPwsChatPanel from '../ServicesPwsChat/ServicesPwsChatPanel';
import { getMrChatProgress, getConstructionSowChatProgress, getServicesPwsChatProgress } from './api';
import { GpcHandoffPending } from './GpcHandoffPending';
import type { HomeChatPanelViewProps } from './HomeChatPanelView';

type Props = Pick<HomeChatPanelViewProps,
  | 'threadScrollRef'
  | 'allowedWorkflowTypes'
  | 'workflowOnlyAudience'
  | 'workflowOnlyPurchaseGuidance'

  | 'busyWorkflowRecommendationCardId'
  | 'chatCards'
  | 'conversationItems'
  | 'failedPurchaseFollowup'
  | 'inFlightUserMsg'
  | 'messages'
  | 'mrNotificationBoundResumeId'
  | 'mrObserverNotificationId'
  | 'newPurchaseGuidanceFocusId'
  | 'pendingGpcRouter'
  | 'setNewPurchaseGuidanceFocusId'
  | 'workflowOnlyChoice'
  | 'workflowOnlyEntryMessage'
  | 'workflowOnlyGuidance'
  | 'constrainedWorkflowEntry'
  | 'onBackConstrainedWorkflowEntry'
  | 'onRetryConstrainedWorkflowEntry'
  | 'workflowOnly'
  | 'currentRaFollowupCard'
  | 'handleCancelSimilarRequirements'
  | 'handleOpenSimilarRequirementsResult'
  | 'handlePurchaseClarification'
  | 'handleSelectAmbiguousIntentChoice'
  | 'handleSelectMissionPartnerGuidance'
  | 'handleSelectPurchaseGuidance'
  | 'handleSelectSimilarRequirementsType'
  | 'handleSelectWorkflowRecommendation'
  | 'notifyBoundMrQuestions'
  | 'rehydrateBoundMrChat'
  | 'shareAndNotifyBoundMrQuestions'
  | 'submitGpcConfirmation'
  | 'submitNotificationBoundMrTurn'
  | 'submitSectionCHandoff'
  | 'submitWorkflowOnlyChoice'

  | 'mrChatView'
  | 'constructionSowChatView'
  | 'servicesPwsChatView'
  | 'gpcChatView'
  | 'optimisticGpcOpening'
  | 'pendingIntentChoiceSelection'
  | 'displayMode'
  | 'isCompanion'
  | 'submitBoundMrTurn'
  | 'activeSessionIdRef'
  | 'activeSessionId'
  | 'uploadMrFiles'
  | 'openMrWorkflow'
  | 'mrTurnInFlight'
  | 'inFlight'
  | 'pendingRequestId'
  | 'processingStopControl'
  | 'completionSoundControl'
  | 'submitBoundServicesPwsTurn'
  | 'uploadServicesRequirement'
  | 'extractServicesRequirement'
  | 'uploadServicesSupport'
  | 'removeServicesRequirement'
  | 'replaceServicesSupport'
  | 'removeServicesSupport'
  | 'openGeneratedServicesPws'
  | 'submitBoundConstructionSowTurn'
  | 'uploadConstructionRequirement'
  | 'extractConstructionRequirement'
  | 'uploadConstructionSupport'
  | 'removeConstructionRequirement'
  | 'replaceConstructionSupport'
  | 'removeConstructionSupport'
  | 'openGeneratedConstructionSow'
  | 'submitBoundGpcTurn'
  | 'openGpcWorkflow'
  | 'rehydratingGpcSessionId'
  | 'gpcHandoffProgressLabel'
  | 'notice'
  | 'setNotice'
> & {
  mobileSessionsNavigation: React.ReactNode;
  desktopSessionsRail: React.ReactNode;
  gpcHydrationPending: boolean;
  sidebarAction?: React.ReactNode;
};

export default function HomeChatBoundWorkflowView({
  sidebarAction,
  mrChatView,
  constructionSowChatView,
  servicesPwsChatView,
  gpcChatView,
  optimisticGpcOpening,
  gpcHydrationPending,
  pendingIntentChoiceSelection,
  displayMode,
  isCompanion,
  mobileSessionsNavigation,
  desktopSessionsRail,
  submitBoundMrTurn,
  activeSessionIdRef,
  activeSessionId,
  uploadMrFiles,
  openMrWorkflow,
  mrTurnInFlight,
  inFlight,
  pendingRequestId,
  processingStopControl,
  completionSoundControl,
  submitBoundServicesPwsTurn,
  uploadServicesRequirement,
  extractServicesRequirement,
  uploadServicesSupport,
  removeServicesRequirement,
  replaceServicesSupport,
  removeServicesSupport,
  openGeneratedServicesPws,
  submitBoundConstructionSowTurn,
  uploadConstructionRequirement,
  extractConstructionRequirement,
  uploadConstructionSupport,
  removeConstructionRequirement,
  replaceConstructionSupport,
  removeConstructionSupport,
  openGeneratedConstructionSow,
  submitBoundGpcTurn,
  openGpcWorkflow,
  rehydratingGpcSessionId,
  gpcHandoffProgressLabel,
  notice,
  setNotice,
  busyWorkflowRecommendationCardId,
  chatCards,
  conversationItems,
  failedPurchaseFollowup,
  inFlightUserMsg,
  messages,
  mrNotificationBoundResumeId,
  mrObserverNotificationId,
  newPurchaseGuidanceFocusId,
  pendingGpcRouter,
  setNewPurchaseGuidanceFocusId,
  workflowOnlyChoice,
  workflowOnlyEntryMessage,
  workflowOnlyGuidance,
  constrainedWorkflowEntry,
  onBackConstrainedWorkflowEntry,
  onRetryConstrainedWorkflowEntry,
  workflowOnly,
  currentRaFollowupCard,
  handleCancelSimilarRequirements,
  handleOpenSimilarRequirementsResult,
  handlePurchaseClarification,
  handleSelectAmbiguousIntentChoice,
  handleSelectMissionPartnerGuidance,
  handleSelectPurchaseGuidance,
  handleSelectSimilarRequirementsType,
  handleSelectWorkflowRecommendation,
  notifyBoundMrQuestions,
  rehydrateBoundMrChat,
  shareAndNotifyBoundMrQuestions,
  submitGpcConfirmation,
  submitNotificationBoundMrTurn,
  submitSectionCHandoff,
  submitWorkflowOnlyChoice,
  threadScrollRef,
  allowedWorkflowTypes,
  workflowOnlyAudience,
  workflowOnlyPurchaseGuidance,
}: Props) {
  const hasWorkflowOnlyGpcConfirmation = workflowOnly && pendingGpcRouter?.kind === 'start_confirmation';
  const workflowOnlyPresentation = getWorkflowOnlyPresentation(allowedWorkflowTypes, workflowOnlyAudience);
  const raPurchaseGuidance = workflowOnlyPurchaseGuidance?.kind === 'mission_partner_guidance' || workflowOnlyAudience === 'ra' || (workflowOnlyAudience !== 'ce'
    && workflowOnlyPresentation?.abilities.some((ability) => ability === 'market_research' || ability === 'gpc'))
    ? workflowOnlyPurchaseGuidance : null;
  const projectedRaChoice = workflowOnlyAudience === 'ra'
    ? normalizeHomeChatCards([workflowOnlyChoice])[0] : null;
  const raWorkflowActionChoice = projectedRaChoice?.kind === 'action_choice' ? projectedRaChoice : null;

  // Only an explicit RA audience can combine procurement and construction.
  // Other malformed projections must not render controllers or saved rows.
  if (workflowOnly && !workflowOnlyPresentation) return null;


    return (
      <WorkflowConversationPresentation.Provider value={{ minimal: isCompanion, action: sidebarAction }}>
      <Paper
        variant="outlined"
        data-testid="home-chat-panel"
        data-tour-id="home-chat-panel"
        data-display-mode={displayMode}
        sx={{
          p: isCompanion ? 0 : 2,
          borderRadius: isCompanion ? 0 : 2,
          border: isCompanion ? 0 : undefined,
          ...(isCompanion ? {
            '& [data-testid="gpc-chat-scroll-region"], & [data-testid="mr-chat-conversation"], & [data-testid="construction-sow-chat-panel"] > [aria-busy], & [data-testid="services-pws-chat-panel"] > [aria-busy]': { p: 0 },
          } : {}),
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {mobileSessionsNavigation}
        <Box sx={{ display: 'flex', gap: isCompanion ? 0 : 2, flex: 1, minHeight: 0 }}>
          {desktopSessionsRail}
          <Box sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            border: isCompanion ? 0 : 1,
            borderColor: 'divider',
            borderRadius: isCompanion ? 0 : 1,
          }}>
            {constrainedWorkflowEntry ? (
              <Box
                data-testid={`home-chat-workflow-only-entry-${constrainedWorkflowEntry.state}`}
                sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
              >
                <HomeChatWorkflowBanner
                  title={workflowOnlyPresentation?.panelTitle || 'Authorized workflow'}
                  subtitle={workflowOnlyPresentation?.panelSubtitle || 'Describe the authorized workflow you need.'}
                  headingComponent="h3"
                />
                <Box
                  ref={threadScrollRef} data-testid="home-chat-thread"
                  sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}
                >
                  {constrainedWorkflowEntry.state === 'pending' ? (
                    <ChatThread
                      compact
                      messages={[{ role: 'user', content: constrainedWorkflowEntry.message }]}
                      loading
                      loadingAriaLabel="Assistant is processing"
                      loadingContent={(
                        <Box role="status" aria-live="polite" aria-label="Assistant is processing" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} aria-hidden="true" />
                          <Typography variant="body2" sx={{ color: 'inherit' }}>Processing your request…</Typography>
                        </Box>
                      )}
                      emptyPlaceholder={null}
                    />
                  ) : (
                    <>
                      <ChatThread
                        compact
                        messages={[
                          { role: 'user', content: constrainedWorkflowEntry.message },
                          { role: 'assistant', content: 'Unable to start this workflow right now. Please try again.\n\nYour request is still available to retry.' },
                        ]}
                        emptyPlaceholder={null}
                      />
                      <Box role="alert" sx={{ display: 'flex', gap: 1, pt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => onRetryConstrainedWorkflowEntry?.(constrainedWorkflowEntry.message)}
                          disabled={!onRetryConstrainedWorkflowEntry}
                        >
                          Retry
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => onBackConstrainedWorkflowEntry?.(constrainedWorkflowEntry.message)}
                          disabled={!onBackConstrainedWorkflowEntry}
                        >
                          Back
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ) : mrChatView ? (
              <MrChatPanel
                initialView={mrChatView}
                previousConversation={conversationItems.length > 0 ? (
                  <Stack spacing={1.5}>
                    {conversationItems.map(item => item.kind === 'legacy_message'
                      ? <ChatThread key={`history-message-${item.index}`} messages={[item.message]} compact emptyPlaceholder={null} />
                      : item.kind === 'card'
                        ? <Box key={`history-card-${item.card.card_id}`} component="fieldset" disabled sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                            <SimilarRequirementsCards cards={[item.card]} busyCardId={null}
                              onSelectType={() => {}} onCancel={() => {}} onOpen={() => {}}
                              onSelectIntentChoice={() => {}} onSelectWorkflowRecommendation={() => {}} onSelectActionChoice={() => {}} />
                          </Box>
                        : <FarChatResponse key={`history-turn-${item.turn.turn_id || item.index}`}
                            turn={{ ...item.turn, editable: false }} workflowId={activeSessionId} threadId={activeSessionId} source="homechat" />)}
                  </Stack>
                ) : undefined}
                onTurn={submitNotificationBoundMrTurn}
                onSectionCHandoff={submitSectionCHandoff}
                onNotifyQuestions={notifyBoundMrQuestions}
                onShareAndNotifyQuestions={shareAndNotifyBoundMrQuestions}
                onPollView={(pollNumber) => mrObserverNotificationId
                  ? getWorkflowNotificationObserverProgress(mrObserverNotificationId)
                  : mrNotificationBoundResumeId
                    ? getWorkflowNotificationResumeProgress(mrNotificationBoundResumeId)
                  : getMrChatProgress(activeSessionIdRef.current || activeSessionId || '', pollNumber)}
                onRehydrate={rehydrateBoundMrChat}
                onUploadFiles={uploadMrFiles}
                onOpenWorkflow={openMrWorkflow}
                initiatingRequest={workflowOnly ? workflowOnlyEntryMessage : null}
                processingRequestId={
                  mrTurnInFlight?.homeSessionId === activeSessionId
                    ? mrTurnInFlight.requestId
                    : inFlight
                      ? pendingRequestId
                      : null
                }
                processingAction={processingStopControl}
                headerAction={completionSoundControl}
              />
            ) : servicesPwsChatView ? (
              <ServicesPwsChatPanel
                key={activeSessionId || servicesPwsChatView.session_id}
                initialView={servicesPwsChatView}
                onTurn={submitBoundServicesPwsTurn}
                onPollView={(pollNumber) => getServicesPwsChatProgress(activeSessionId || '', pollNumber)}
                onUploadRequirementDocument={uploadServicesRequirement}
                onExtractRequirementDocument={extractServicesRequirement}
                onUploadArtifact={uploadServicesSupport}
                onRemoveRequirementDocument={removeServicesRequirement}
                onReplaceArtifact={replaceServicesSupport}
                onRemoveArtifact={removeServicesSupport}
                onViewGeneratedPws={openGeneratedServicesPws}
                processingAction={processingStopControl}
                headerAction={completionSoundControl}
              />
            ) : constructionSowChatView ? (
              <ConstructionSowChatPanel
                key={activeSessionId || constructionSowChatView.session_id}
                initialView={constructionSowChatView}
                entryMessage={workflowOnly ? workflowOnlyEntryMessage : null}
                onTurn={submitBoundConstructionSowTurn}
                onPollView={(pollNumber) => getConstructionSowChatProgress(
                  activeSessionId || '',
                  pollNumber,
                )}
                onUploadRequirementDocument={uploadConstructionRequirement}
                onExtractRequirementDocument={extractConstructionRequirement}
                onUploadArtifact={uploadConstructionSupport}
                onRemoveRequirementDocument={removeConstructionRequirement}
                onReplaceArtifact={replaceConstructionSupport}
                onRemoveArtifact={removeConstructionSupport}
                onViewGeneratedSow={openGeneratedConstructionSow}
                processingAction={processingStopControl}
                headerAction={completionSoundControl}
              />
            ) : gpcChatView ? (
              <GpcChatPanel
                initialView={gpcChatView}
                onTurn={submitBoundGpcTurn}
                optimisticOpeningMessage={optimisticGpcOpening || (workflowOnly ? workflowOnlyEntryMessage : null)}
                onOpenWorkflow={openGpcWorkflow}
                rehydrating={rehydratingGpcSessionId === activeSessionId}
                processingRequestId={inFlight ? pendingRequestId : null}
                processingAction={processingStopControl}
                headerAction={completionSoundControl}
              />
            ) : hasWorkflowOnlyGpcConfirmation ? (
              <Box sx={{ p: 3 }} data-testid="home-chat-workflow-only-gpc-confirmation">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {pendingGpcRouter.originalMessage}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{pendingGpcRouter.prompt}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    disabled={inFlight}
                    onClick={() => void submitGpcConfirmation('confirmed')}
                  >
                    Confirm and continue
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={inFlight}
                    onClick={() => void submitGpcConfirmation('revise')}
                  >
                    Revise
                  </Button>
                </Box>
              </Box>
            ) : raPurchaseGuidance || raWorkflowActionChoice ? (
              <Box data-testid="home-chat-workflow-only-purchase-guidance" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <HomeChatWorkflowBanner
                  title={workflowOnlyPresentation?.panelTitle || 'Authorized workflow'}
                  subtitle={workflowOnlyPresentation?.panelSubtitle || 'Review the approved purchase route.'}
                  headingComponent="h3"
                />
                <Box ref={threadScrollRef} data-testid="home-chat-thread" sx={{ p: 2, overflowY: 'auto', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {messages.length === 0 && workflowOnlyEntryMessage &&
                    <ChatThread compact messages={[{ role: 'user', content: workflowOnlyEntryMessage }]} emptyPlaceholder={null} />}
                  {mergeHomeChatConversation(
                    messages,
                    (chatCards.filter((item) => item.kind === 'purchase_guidance' || item.kind === 'mission_partner_guidance' || item.kind === 'action_choice').length
                      ? chatCards.filter((item) => item.kind === 'purchase_guidance' || item.kind === 'mission_partner_guidance' || item.kind === 'action_choice')
                      : [raPurchaseGuidance || raWorkflowActionChoice!]),
                    [],
                  ).map((item) => item.kind === 'legacy_message' ? (
                    <ChatThread key={`ra-message-${item.index}-${item.timelineSequence ?? 'legacy'}`}
                      compact messages={[item.message]} emptyPlaceholder={null} />
                  ) : item.kind === 'card' && (item.card.kind === 'purchase_guidance' || item.card.kind === 'mission_partner_guidance' || item.card.kind === 'action_choice') ? (
                    <SimilarRequirementsCards key={`ra-card-${item.card.card_id}`}
                      cards={[item.card.card_id === (currentRaFollowupCard?.card_id || raPurchaseGuidance?.card_id || raWorkflowActionChoice?.card_id)
                        ? item.card : { ...item.card, options: [] }]}
                      busyCardId={busyWorkflowRecommendationCardId}
                      focusNewPurchaseGuidanceCardId={newPurchaseGuidanceFocusId}
                      onPurchaseGuidanceHeadingFocused={() => setNewPurchaseGuidanceFocusId(null)}
                      onSelectType={handleSelectSimilarRequirementsType} onCancel={handleCancelSimilarRequirements}
                      onOpen={handleOpenSimilarRequirementsResult} onSelectIntentChoice={handleSelectAmbiguousIntentChoice}
                      onSelectWorkflowRecommendation={handleSelectWorkflowRecommendation}
                      onSelectActionChoice={(selectedCard, selectionKey) => {
                        if (selectedCard.card_id === raWorkflowActionChoice?.card_id) {
                          void submitWorkflowOnlyChoice(selectionKey);
                        }
                      }}
                      onSelectMissionPartnerGuidance={handleSelectMissionPartnerGuidance}
                      onSelectPurchaseGuidance={handleSelectPurchaseGuidance} />
                  ) : null)}
                  {inFlightUserMsg && <ChatThread compact
                    messages={messages.some((item) =>
                      currentRaFollowupCard !== null
                      && item.timeline_sequence === currentRaFollowupCard.timeline_sequence - 1
                      && item.content === inFlightUserMsg,
                    ) ? [] : [{ role: 'user', content: inFlightUserMsg }]}
                    loading emptyPlaceholder={null} />}
                  {failedPurchaseFollowup && !inFlight && <Box role="alert" sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                    <ChatThread compact messages={[{ role: 'user', content: failedPurchaseFollowup.message }]} emptyPlaceholder={null} />
                    <Typography variant="body2">That follow-up did not complete. You can retry it here.</Typography>
                    <Button variant="outlined" onClick={() => void handlePurchaseClarification(failedPurchaseFollowup.message, failedPurchaseFollowup.requestId)}>Retry follow-up</Button>
                  </Box>}
                </Box>
                {currentRaFollowupCard && <Box sx={{ flexShrink: 0 }}>
                  <PurchaseFollowupInput onSend={handlePurchaseClarification}
                    placeholder="Ask a follow-up about what you need…"
                    disabled={inFlight} />
                </Box>}
              </Box>
            ) : workflowOnlyChoice ? (
              <Box data-testid="home-chat-workflow-only-choice" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <HomeChatWorkflowBanner
                  title={workflowOnlyPresentation?.panelTitle || 'Authorized workflow'}
                  subtitle={workflowOnlyPresentation?.panelSubtitle || 'Choose the authorized workflow you need.'}
                  headingComponent="h3"
                />
                <Box ref={threadScrollRef} data-testid="home-chat-thread" sx={{ p: 2, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {workflowOnlyEntryMessage && <ChatThread compact messages={[{ role: 'user', content: workflowOnlyEntryMessage }]} emptyPlaceholder={null} />}
                  <Paper elevation={0} data-role="assistant" sx={{ alignSelf: 'flex-start', maxWidth: '85%', px: 1.5, py: 1.25, borderRadius: 2, bgcolor: 'surfaceMuted', color: 'text.primary', boxShadow: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {typeof workflowOnlyChoice.prompt === 'string'
                        ? workflowOnlyChoice.prompt
                        : 'Choose the workflow you want to start.'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                      {Array.isArray(workflowOnlyChoice.options) && workflowOnlyChoice.options.map((option) => (
                        option && typeof option === 'object'
                        && typeof (option as Record<string, unknown>).label === 'string'
                        && typeof (option as Record<string, unknown>).selection_key === 'string'
                          ? <Button
                              key={(option as Record<string, string>).selection_key}
                              variant="contained"
                              disabled={inFlight}
                              onClick={() => void submitWorkflowOnlyChoice((option as Record<string, string>).selection_key)}
                            >
                              {(option as Record<string, string>).label}
                            </Button>
                          : null
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            ) : workflowOnlyGuidance ? (
              <Box data-testid="home-chat-workflow-only-guidance" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <HomeChatWorkflowBanner
                  title={workflowOnlyPresentation?.panelTitle || 'Authorized workflow'}
                  subtitle={workflowOnlyPresentation?.panelSubtitle || 'Describe the authorized workflow you need.'}
                  headingComponent="h3"
                />
                <Box ref={threadScrollRef} data-testid="home-chat-thread" sx={{ p: 2, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {workflowOnlyEntryMessage && <ChatThread compact messages={[{ role: 'user', content: workflowOnlyEntryMessage }]} emptyPlaceholder={null} />}
                  <ChatThread compact messages={[{ role: 'assistant', content: workflowOnlyGuidance }]} emptyPlaceholder={null} />
                </Box>
              </Box>
            ) : (optimisticGpcOpening || pendingIntentChoiceSelection?.path === 'gpc') ? (
              <GpcHandoffPending
                openingMessage={optimisticGpcOpening || pendingIntentChoiceSelection?.openingMessage || 'Preparing your GPC purchase.'}
                progressLabel={gpcHandoffProgressLabel}
                processingAction={processingStopControl}
                headerAction={completionSoundControl}
              />
            ) : pendingIntentChoiceSelection?.path === 'mr' ? (
              <MrChatPanel
                rehydrating
                initiatingRequest={pendingIntentChoiceSelection.openingMessage}
                processingAction={processingStopControl}
                headerAction={completionSoundControl}
              />
            ) : (
              // A cold mount knows only the durable Home Chat ↔ GPC binding
              // until `/thread` returns. Keep the user in a GPC shell with a
              // clear loading indicator instead of the empty Q&A pane.
              <GpcChatPanel rehydrating processingAction={processingStopControl} headerAction={completionSoundControl} />
            )}
          </Box>
        </Box>
        <Snackbar
          open={notice !== null}
          autoHideDuration={4000}
          onClose={() => setNotice(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {notice ? (
            <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">
              {notice.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Paper>
      </WorkflowConversationPresentation.Provider>
    );
}
